import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { CustomerAuthService, TenantContext, TenantMembershipsStore } from '@pryspace/shared/api';

/**
 * Guards `/tenants/:slug/**`. Requires:
 *  1. a valid customer session (refreshes once if expired)
 *  2. the user has an active membership on `:slug`
 *
 * Reads memberships from the cached store so navigation doesn't hit the
 * network — only the first call to `ensureLoaded()` triggers a request.
 */
export const tenantMembershipGuard: CanActivateFn = async (route) => {
  const auth = inject(CustomerAuthService);
  const store = inject(TenantMembershipsStore);
  const tenantCtx = inject(TenantContext);
  const router = inject(Router);

  const slug = route.paramMap.get('slug') ?? route.params['slug'];
  if (!slug) {
    await router.navigate(['/']);
    return false;
  }

  if (!auth.isAuthenticated()) {
    const refreshed = await auth.refresh();
    if (!refreshed) {
      await router.navigate(['/login']);
      return false;
    }
  }

  try {
    await store.ensureLoaded();
  } catch {
    await router.navigate(['/login']);
    return false;
  }

  if (!store.hasMembership(slug)) {
    await router.navigate(['/']);
    return false;
  }

  tenantCtx.set(slug);
  return true;
};

// Backwards-compatible alias — old route configs still imported the previous name.
export const tenantAuthGuard = tenantMembershipGuard;
